import React from "react";
import { useFunnel } from "../../context/FunnelContext";
import { StepHeader } from "../common/StepHeader";
import { Footer } from "../common/Footer";
import { useFunnelNavigation } from "../../hooks/useFunnelNavigation";

export function WeightPrediction({ config }) {
  const { state } = useFunnel();
  const { nextStep } = useFunnelNavigation(); // Make sure this is imported
  const prediction = calculatePrediction(state.userData);

  const formattedDate = prediction.targetDate.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
  });

  const dataPoints = [
    { month: "Now", weight: prediction.currentWeight },
    {
      month: prediction.targetDate.toLocaleDateString("en-US", {
        month: "short",
      }),
      weight: prediction.goalWeight,
    },
  ];

  return (
    <div className="px-4">
      {/* Existing content */}
      <StepHeader title={config.title} subtitle={config.subtitle} />
      <PredictionHeader prediction={prediction} formattedDate={formattedDate} />

      <div className="bg-white rounded-2xl shadow-sm ">
        <div className="p-8 space-y-6">
          <Graph
            dataPoints={dataPoints}
            gradientColors={{
              primary: "#22c55e",
              secondary: "#3b82f6",
            }}
          />
        </div>
      </div>

      {/* Replace the existing button with Footer component */}
      <Footer onClick={nextStep}>{config.buttonText || "Next"}</Footer>
    </div>
  );
}

export function Graph({
  dataPoints,
  height = "h-64",
  gradientColors = {
    primary: "#22c55e",
    secondary: "#3b82f6",
  },
}) {
  const graphRanges = calculateGraphRanges(
    dataPoints[0].weight,
    dataPoints[dataPoints.length - 1].weight,
  );

  const getYPosition = (weight) => graphRanges.weightToY(weight);
  const isDecreasing =
    dataPoints[0].weight > dataPoints[dataPoints.length - 1].weight;

  return (
    <div className={`${height} relative pb-4`}>
      <div className="h-full relative m-2">
        <svg
          className="w-full h-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
              {isDecreasing ? (
                <>
                  <stop
                    offset="0%"
                    stopColor={gradientColors.primary}
                    stopOpacity="0.3"
                  />
                  <stop
                    offset="100%"
                    stopColor={gradientColors.primary}
                    stopOpacity="0.05"
                  />
                </>
              ) : (
                <>
                  <stop
                    offset="0%"
                    stopColor={gradientColors.secondary}
                    stopOpacity="0.3"
                  />
                  <stop
                    offset="100%"
                    stopColor={gradientColors.secondary}
                    stopOpacity="0.05"
                  />
                </>
              )}
            </linearGradient>
          </defs>

          <GridLines />

          <GraphPath
            startY={getYPosition(dataPoints[0].weight)}
            endY={getYPosition(dataPoints[dataPoints.length - 1].weight)}
            isDecreasing={isDecreasing}
            gradientColors={gradientColors}
          />
        </svg>

        <DataPoints
          dataPoints={dataPoints}
          getYPosition={getYPosition}
          isDecreasing={isDecreasing}
          gradientColors={gradientColors}
        />

        {/* Weight labels */}
        {dataPoints.map((point, index) => (
          <div
            key={index}
            className="absolute font-bold"
            style={{
              left: `${index === 0 ? 0 : 100}%`,
              top: `${getYPosition(point.weight)}%`,
              transform: "translate(-50%, -150%)",
              marginBottom: "10px",
            }}
          >
            <span className="bg-gray-100 px-2 py-1 rounded-full">
              {point.weight}kg
            </span>
          </div>
        ))}

        {/* X-axis labels */}
        <div
          className="absolute w-full flex justify-between text-sm font-medium text-gray-600"
          style={{
            bottom: "-30px",
            left: "0",
            right: "0",
          }}
        >
          {dataPoints.map((point, index) => (
            <span key={index} className="bg-gray-50 px-3 py-1 rounded-full">
              {point.month}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export const calculatePrediction = (userData) => {
  const currentWeight = userData.weight?.weight || 80;
  const goalWeight = userData.weightGoal?.weight || 70;
  const weightDiff = Math.abs(currentWeight - goalWeight);
  const isWeightLoss = currentWeight > goalWeight;

  const weeksToGoal = Math.ceil(weightDiff / 0.75);
  const monthsToGoal = Math.ceil(weeksToGoal / 4.345);

  const targetDate = new Date();
  targetDate.setDate(targetDate.getDate() + weeksToGoal * 7);

  return {
    currentWeight,
    goalWeight,
    targetDate,
    weightDiff,
    isWeightLoss,
    months: monthsToGoal,
  };
};

export const calculateGraphRanges = (currentWeight, goalWeight) => {
  const totalRange = Math.abs(currentWeight - goalWeight);
  const padding = totalRange * 0.1;
  const maxWeight = Math.max(currentWeight, goalWeight) + padding;
  const minWeight = Math.min(currentWeight, goalWeight) - padding;
  const range = maxWeight - minWeight;

  const weightToY = (weight) => {
    return 100 - ((weight - minWeight) / range) * 100;
  };

  return { maxWeight, minWeight, range, weightToY };
};

export const calculateControlPoints = (startX, startY, endX, endY) => {
  const controlX1 = startX + (endX - startX) / 3;
  const controlX2 = startX + (2 * (endX - startX)) / 3;
  const controlY1 = startY;
  const controlY2 = endY;
  return { controlX1, controlY1, controlX2, controlY2 };
};

export function PredictionHeader({ prediction, formattedDate }) {
  return (
    <div className="text-center mb-8">
      <p className="font-black text-4xl mb-2">
        {prediction.isWeightLoss ? (
          <span className="bg-gradient-to-r from-green-500 to-emerald-600 text-transparent bg-clip-text">
            Loss {prediction.weightDiff.toFixed(0)} kg
          </span>
        ) : (
          <span className="bg-gradient-to-r from-blue-500 to-indigo-600 text-transparent bg-clip-text">
            Gain {prediction.weightDiff.toFixed(0)} kg
          </span>
        )}
      </p>
      <p className="text-gray-600 text-xl">
        by <span className="font-semibold">{formattedDate}</span>
      </p>
    </div>
  );
}

export function GridLines() {
  return (
    <>
      {[0, 25, 50, 75, 100].map((position) => (
        <line
          key={`grid-${position}`}
          x1={position}
          y1="0"
          x2={position}
          y2="100"
          stroke="#f1f5f9"
          strokeWidth="1"
          strokeDasharray="1"
        />
      ))}
    </>
  );
}

export function GraphPath({ startY, endY, isDecreasing, gradientColors }) {
  const startX = 0;
  const endX = 100;

  const { controlX1, controlY1, controlX2, controlY2 } = calculateControlPoints(
    startX,
    startY,
    endX,
    endY,
  );

  const curvePath = `
    M ${startX},${startY}
    C ${controlX1},${controlY1}
      ${controlX2},${controlY2}
      ${endX},${endY}
    L ${endX},100
    L ${startX},100 Z
  `;

  const linePath = `
    M ${startX},${startY}
    C ${controlX1},${controlY1}
      ${controlX2},${controlY2}
      ${endX},${endY}
  `;

  return (
    <>
      <path d={curvePath} fill="url(#gradient)" />
      <path
        d={linePath}
        stroke={
          isDecreasing ? gradientColors.primary : gradientColors.secondary
        }
        strokeWidth="2"
        fill="none"
      />
    </>
  );
}

export function DataPoints({
  dataPoints,
  getYPosition,
  isDecreasing,
  gradientColors,
}) {
  return (
    <>
      {dataPoints.map((point, index) => {
        const x = index === 0 ? 0 : 100;
        const y = getYPosition(point.weight);
        return (
          <div
            key={index}
            className="absolute w-4 h-4 rounded-full shadow-lg border-2 border-white"
            style={{
              left: `${x}%`,
              top: `${y}%`,
              transform: "translate(-50%, -50%)",
              backgroundColor: isDecreasing
                ? gradientColors.primary
                : gradientColors.secondary,
            }}
          />
        );
      })}
    </>
  );
}
